import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Document, Page } from "react-pdf"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap"
import { toast } from "react-toastify"

import PageWrapper from "components/Common/PageWrapper"
import { useRtgsBankTemplate, useEditRtgsBankTemplate } from "hooks/useRtgsBankTemplate"
import {
  getRtgsBankTemplateFile,
  previewRtgsBankTemplate,
} from "api/rtgsBankTemplateApi"
import "utils/pdfWorker"

import {
  A4_HEIGHT_MM,
  A4_WIDTH_MM,
  COORD_FIELDS,
  DEFAULT_COORDS,
  buildCoords,
} from "./rtgsTemplateFields"

const PT_TO_MM = 0.352778
const CSS_PX_TO_MM = 0.264583 // 1 CSS px at 96dpi
const MAX_RENDER_WIDTH = 820

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)
const round1 = value => Math.round(value * 10) / 10

const RtgsBankTemplateConfigure = () => {
  document.title = "Configure RTGS Pad"

  const { id } = useParams()
  const navigate = useNavigate()

  const { data: templateResponse, isLoading: templateLoading } =
    useRtgsBankTemplate(id)
  const template = templateResponse?.data

  const { mutateAsync: editMutation, isPending: saving } =
    useEditRtgsBankTemplate()

  const [coords, setCoords] = useState(() => buildCoords(null))
  const [activeKey, setActiveKey] = useState(COORD_FIELDS[0].key)
  const [pdfData, setPdfData] = useState(null)
  const [pdfError, setPdfError] = useState(null)
  const [loadingPdf, setLoadingPdf] = useState(true)
  const [renderWidth, setRenderWidth] = useState(MAX_RENDER_WIDTH)
  const [previewLoading, setPreviewLoading] = useState(false)

  const containerRef = useRef(null)
  const dragRef = useRef(null)

  useEffect(() => {
    if (template?.overlay_config) {
      setCoords(buildCoords(template.overlay_config))
    }
  }, [template])

  useEffect(() => {
    let cancelled = false
    setLoadingPdf(true)
    setPdfError(null)
    getRtgsBankTemplateFile(id)
      .then(buffer => {
        if (!cancelled) {
          setPdfData(new Uint8Array(buffer))
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPdfError(
            "Could not load the pad template. Upload a pad template first."
          )
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingPdf(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [id])

  useEffect(() => {
    const measure = () => {
      if (containerRef.current) {
        setRenderWidth(
          Math.min(containerRef.current.offsetWidth, MAX_RENDER_WIDTH)
        )
      }
    }
    measure()
    window.addEventListener("resize", measure)
    return () => window.removeEventListener("resize", measure)
  }, [])

  const pdfFile = useMemo(
    () => (pdfData ? { data: pdfData } : null),
    [pdfData]
  )

  // px-per-mm at the current render width (A4 portrait).
  const scale = renderWidth / A4_WIDTH_MM
  const pageHeight = renderWidth * (A4_HEIGHT_MM / A4_WIDTH_MM)

  const updateField = useCallback((key, patch) => {
    setCoords(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }))
  }, [])

  const handlePointerMove = useCallback(
    event => {
      const drag = dragRef.current
      if (!drag) return
      const rect = drag.overlay.getBoundingClientRect()
      const leftMm = (event.clientX - rect.left - drag.offsetX) / scale
      const topMm = (event.clientY - rect.top - drag.offsetY) / scale
      updateField(drag.key, {
        left: round1(clamp(leftMm, 0, A4_WIDTH_MM)),
        top: round1(clamp(topMm, 0, A4_HEIGHT_MM)),
      })
    },
    [scale, updateField]
  )

  const stopDrag = useCallback(() => {
    dragRef.current = null
    window.removeEventListener("mousemove", handlePointerMove)
    window.removeEventListener("mouseup", stopDrag)
  }, [handlePointerMove])

  const startDrag = useCallback(
    (event, key) => {
      event.preventDefault()
      setActiveKey(key)
      const overlay = containerRef.current?.querySelector(".pad-overlay")
      if (!overlay) return
      const chipRect = event.currentTarget.getBoundingClientRect()
      dragRef.current = {
        key,
        overlay,
        offsetX: event.clientX - chipRect.left,
        offsetY: event.clientY - chipRect.top,
      }
      window.addEventListener("mousemove", handlePointerMove)
      window.addEventListener("mouseup", stopDrag)
    },
    [handlePointerMove, stopDrag]
  )

  useEffect(() => () => stopDrag(), [stopDrag])

  const handleSave = async () => {
    const payload = {
      overlay_config: {},
    }
    if (template?.bank_name) {
      payload.bank_name = template.bank_name
    }
    payload.is_active = template?.is_active ? 1 : 0
    COORD_FIELDS.forEach(({ key }) => {
      const c = coords[key]
      payload.overlay_config[key] = {
        left: c.left,
        top: c.top,
        font_size: c.font_size,
        letter_spacing: c.letter_spacing,
        bold: c.bold ? 1 : 0,
      }
    })
    try {
      const res = await editMutation({ id, data: payload })
      toast.success(res?.message ?? "Pad layout saved successfully.")
    } catch {
      // axios interceptor handles error toast
    }
  }

  const handlePreview = async () => {
    await previewRtgsBankTemplate(id, {
      onStart: () => setPreviewLoading(true),
      onEnd: () => setPreviewLoading(false),
    })
  }

  const active = coords[activeKey]
  const activeLabel = COORD_FIELDS.find(f => f.key === activeKey)?.label

  return (
    <PageWrapper
      title="RTGS Pad Configuration"
      breadcrumbItem={template?.bank_name ?? "Configure Pad"}
      cardBodyClassName="p-2 p-sm-3"
    >
      <Row className="mb-3 gy-2 align-items-center">
        <Col xs={12} sm={6}>
          <h5 className="mb-0">
            {template?.bank_name ?? "—"}{" "}
            <Badge color="info" className="ms-1">
              Visual Pad Editor
            </Badge>
          </h5>
          <small className="text-muted">
            Drag each field onto the pad. Fine-tune on the right. Verify with
            Preview PDF.
          </small>
        </Col>
        <Col
          xs={12}
          sm={6}
          className="d-flex flex-wrap justify-content-start justify-content-sm-end gap-1"
        >
          <Button color="light" onClick={() => navigate("/rtgs-bank-template-list")}>
            <i className="mdi mdi-arrow-left" />
            <span className="d-none d-sm-inline ms-1">Back</span>
          </Button>
          <Button color="info" outline onClick={handlePreview} disabled={previewLoading}>
            {previewLoading ? (
              <Spinner size="sm" />
            ) : (
              <i className="mdi mdi-file-eye-outline" />
            )}
            <span className="d-none d-sm-inline ms-1">Preview PDF</span>
          </Button>
          <Button color="success" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size="sm" /> : <i className="mdi mdi-content-save" />}
            <span className="d-none d-sm-inline ms-1">Save Layout</span>
          </Button>
        </Col>
      </Row>

      <Row className="gy-3">
        <Col lg={8}>
          <Card className="mb-0">
            <CardBody className="p-1 p-sm-3">
              <div ref={containerRef} className="position-relative mx-auto" style={{ maxWidth: MAX_RENDER_WIDTH }}>
                {templateLoading || loadingPdf ? (
                  <div className="text-center py-5">
                    <Spinner /> <span className="ms-2">Loading pad…</span>
                  </div>
                ) : pdfError ? (
                  <div className="alert alert-warning mb-0">{pdfError}</div>
                ) : (
                  <div className="position-relative" style={{ width: renderWidth, height: pageHeight }}>
                    <Document
                      file={pdfFile}
                      loading={<div className="text-center py-5"><Spinner /></div>}
                      error={<div className="alert alert-danger mb-0">Failed to render PDF.</div>}
                    >
                      <Page
                        pageNumber={1}
                        width={renderWidth}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                    </Document>

                    <div
                      className="pad-overlay position-absolute top-0 start-0"
                      style={{ width: renderWidth, height: pageHeight }}
                    >
                      {COORD_FIELDS.map(({ key, sample }) => {
                        const c = coords[key]
                        const isActive = key === activeKey
                        return (
                          <div
                            key={key}
                            onMouseDown={e => startDrag(e, key)}
                            title={sample}
                            style={{
                              position: "absolute",
                              left: c.left * scale,
                              top: c.top * scale,
                              fontSize: c.font_size * PT_TO_MM * scale,
                              letterSpacing: c.letter_spacing * CSS_PX_TO_MM * scale,
                              fontWeight: c.bold ? "bold" : "normal",
                              whiteSpace: "nowrap",
                              cursor: "move",
                              color: "#1d4ed8",
                              background: isActive
                                ? "rgba(255,221,87,0.55)"
                                : "rgba(59,130,246,0.12)",
                              outline: isActive
                                ? "1px solid #f59e0b"
                                : "1px dashed rgba(59,130,246,0.6)",
                              padding: "0 1px",
                              userSelect: "none",
                            }}
                          >
                            {sample}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="mb-0">
            <CardBody className="p-2 p-sm-3">
              <h6 className="mb-3">Fields</h6>
              <div className="d-flex flex-wrap gap-1 mb-3">
                {COORD_FIELDS.map(({ key, label }) => (
                  <Button
                    key={key}
                    size="sm"
                    color={key === activeKey ? "primary" : "light"}
                    onClick={() => setActiveKey(key)}
                  >
                    {label}
                  </Button>
                ))}
              </div>

              {active && (
                <>
                  <div className="border-top pt-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong>{activeLabel}</strong>
                      <Button
                        size="sm"
                        color="link"
                        className="p-0"
                        onClick={() =>
                          updateField(activeKey, { ...DEFAULT_COORDS[activeKey] })
                        }
                      >
                        Reset field
                      </Button>
                    </div>
                    <Row className="g-2">
                      <Col xs={6}>
                        <Label className="small mb-1">Left (mm)</Label>
                        <Input
                          type="number"
                          step="0.5"
                          value={active.left}
                          onChange={e =>
                            updateField(activeKey, {
                              left: clamp(+e.target.value, 0, A4_WIDTH_MM),
                            })
                          }
                        />
                      </Col>
                      <Col xs={6}>
                        <Label className="small mb-1">Top (mm)</Label>
                        <Input
                          type="number"
                          step="0.5"
                          value={active.top}
                          onChange={e =>
                            updateField(activeKey, {
                              top: clamp(+e.target.value, 0, A4_HEIGHT_MM),
                            })
                          }
                        />
                      </Col>
                      <Col xs={6}>
                        <Label className="small mb-1">Font size (pt)</Label>
                        <Input
                          type="number"
                          step="0.5"
                          value={active.font_size}
                          onChange={e =>
                            updateField(activeKey, {
                              font_size: clamp(+e.target.value, 4, 72),
                            })
                          }
                        />
                      </Col>
                      <Col xs={6}>
                        <Label className="small mb-1">Letter spacing (px)</Label>
                        <Input
                          type="number"
                          step="1"
                          value={active.letter_spacing}
                          onChange={e =>
                            updateField(activeKey, {
                              letter_spacing: clamp(+e.target.value, 0, 200),
                            })
                          }
                        />
                      </Col>
                      <Col xs={12}>
                        <Button
                          type="button"
                          color={active.bold ? "success" : "secondary"}
                          outline={!active.bold}
                          size="sm"
                          className="mt-1"
                          onClick={() =>
                            updateField(activeKey, { bold: !active.bold })
                          }
                        >
                          <i
                            className={`bx ${
                              active.bold ? "bx-check-circle" : "bx-x-circle"
                            } me-1`}
                          />
                          {active.bold ? "Bold On" : "Bold Off"}
                        </Button>
                      </Col>
                    </Row>
                    <small className="text-muted d-block mt-2">
                      Boxed fields (account / mobile numbers) use letter spacing
                      to align each digit into its cell.
                    </small>
                  </div>
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </PageWrapper>
  )
}

export default RtgsBankTemplateConfigure
